import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert } from "../../components/Alert";
import { ItemsEditor } from "../../components/billing/ItemsEditor";
import { RequirePermission } from "../../components/crm/RequirePermission";
import { Spinner } from "../../components/Spinner";
import {
  billingErrorCode,
  createInvoice,
  getInvoice,
  isApiError,
  listAccounts,
  listProducts,
  listTaxes,
  updateInvoice,
} from "../../lib/billing-api";
import {
  draftFromItem,
  emptyItemDraft,
  toDocumentItemInputs,
  type ItemDraft,
} from "../../lib/billing-items";
import type { Account, InvoiceInput, Product, Tax } from "../../lib/billing-types";
import type { TranslationKey } from "../../lib/i18n";
import { useConfig } from "../../state/config-context";

const ERROR_KEYS: Record<string, TranslationKey> = {
  invalid_state_transition: "billing.error.invalidTransition",
  "billing.error.invalid_state_transition": "billing.error.invalidTransition",
  "billing.error.currency_mismatch": "billing.error.currencyMismatch",
  billing_item_immutable: "billing.error.itemImmutable",
  billing_invoice_not_found: "billing.error.notFound",
};

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function defaultDue(): string {
  const date = new Date();
  date.setDate(date.getDate() + 30);
  return date.toISOString().slice(0, 10);
}

function InvoiceFormInner({ id }: { id: string | undefined }): ReactNode {
  const { t } = useConfig();
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [accountId, setAccountId] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [issueDate, setIssueDate] = useState(today());
  const [dueDate, setDueDate] = useState(defaultDue());
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState<ItemDraft[]>([emptyItemDraft("item-0")]);
  const [loading, setLoading] = useState(id !== undefined);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [accountPage, productPage, taxPage] = await Promise.all([
          listAccounts(),
          listProducts(),
          listTaxes(),
        ]);
        if (!cancelled) {
          setAccounts(accountPage.items);
          setProducts(productPage.items);
          setTaxes(taxPage.items);
        }
      } catch (caught) {
        if (!cancelled) {
          setError(isApiError(caught) ? caught.detail : t("error.network"));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    if (id === undefined) {
      return () => {
        cancelled = true;
      };
    }
    void (async () => {
      setLoading(true);
      try {
        const invoice = await getInvoice(id);
        if (cancelled) {
          return;
        }
        if (invoice.status !== "draft") {
          setError(t("billing.form.invoiceNotEditable"));
          return;
        }
        setAccountId(invoice.account_id);
        setCurrency(invoice.currency);
        setIssueDate(invoice.issue_date.slice(0, 10));
        setDueDate(invoice.due_date.slice(0, 10));
        setItems(
          invoice.items.length === 0
            ? [emptyItemDraft("item-0")]
            : invoice.items.map((item, index) => draftFromItem(item, `item-${String(index)}`)),
        );
      } catch (caught) {
        if (!cancelled) {
          setError(isApiError(caught) ? caught.detail : t("error.network"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    const input: InvoiceInput = {
      account_id: accountId,
      currency: currency.trim().toUpperCase(),
      issue_date: issueDate,
      due_date: dueDate,
      notes: notes.trim() === "" ? null : notes.trim(),
      items: toDocumentItemInputs(items, taxes),
    };
    try {
      if (id === undefined) {
        const invoice = await createInvoice(input);
        navigate(`/billing/invoices/${invoice.id}`, { replace: true });
      } else {
        await updateInvoice(id, input);
        navigate(`/billing/invoices/${id}`, { replace: true });
      }
    } catch (caught) {
      const code = billingErrorCode(caught);
      const key = code === null ? undefined : ERROR_KEYS[code];
      setError(
        key !== undefined ? t(key) : isApiError(caught) ? caught.detail : t("error.network"),
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Spinner label={t("app.loading")} />;
  }

  return (
    <section className="page" aria-labelledby="billing-invoice-form-title">
      <header className="page__header">
        <p className="muted">
          <Link to="/billing/invoices">{t("billing.invoices.title")}</Link>
        </p>
        <h1 id="billing-invoice-form-title">
          {id === undefined ? t("billing.form.createInvoice") : t("billing.form.editInvoice")}
        </h1>
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}

      <form className="form form--inline-card" onSubmit={(event) => void onSubmit(event)}>
        <div className="form-grid">
          <label className="field" htmlFor="invoice-account">
            <span>{t("billing.invoice.account")}</span>
            <select
              id="invoice-account"
              value={accountId}
              required
              onChange={(event) => {
                const next = event.target.value;
                setAccountId(next);
                const account = accounts.find((row) => row.id === next);
                if (account !== undefined) {
                  setCurrency(account.currency);
                }
              }}
            >
              <option value="">{t("billing.form.accountRequired")}</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name} · {account.currency}
                </option>
              ))}
            </select>
          </label>
          <label className="field" htmlFor="invoice-currency">
            <span>{t("billing.common.currency")}</span>
            <input
              id="invoice-currency"
              value={currency}
              maxLength={3}
              required
              onChange={(event) => setCurrency(event.target.value)}
            />
          </label>
          <label className="field" htmlFor="invoice-issue">
            <span>{t("billing.invoice.issueDate")}</span>
            <input
              id="invoice-issue"
              type="date"
              value={issueDate}
              onChange={(event) => setIssueDate(event.target.value)}
            />
          </label>
          <label className="field" htmlFor="invoice-due">
            <span>{t("billing.invoice.dueDate")}</span>
            <input
              id="invoice-due"
              type="date"
              value={dueDate}
              required
              onChange={(event) => setDueDate(event.target.value)}
            />
          </label>
          <label className="field" htmlFor="invoice-notes">
            <span>{t("billing.invoice.notes")}</span>
            <textarea
              id="invoice-notes"
              value={notes}
              rows={3}
              maxLength={5000}
              onChange={(event) => setNotes(event.target.value)}
            />
          </label>
        </div>

        <h2 className="section-title">{t("billing.invoice.items")}</h2>
        <ItemsEditor
          items={items}
          products={products}
          taxes={taxes}
          busy={saving}
          onChange={setItems}
        />
        <p className="muted">{t("billing.item.totalsHint")}</p>

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={saving || accountId === "" || items.length === 0 || dueDate === ""}
          >
            {saving ? t("billing.form.submitting") : t("billing.form.submit")}
          </button>
          <Link className="btn btn--ghost" to="/billing/invoices">
            {t("common.cancel")}
          </Link>
        </div>
      </form>
    </section>
  );
}

function InvoiceFormRoute(): ReactNode {
  const { id } = useParams<{ id: string }>();
  return (
    <RequirePermission
      permission={id === undefined ? "billing.invoice.create" : "billing.invoice.update"}
    >
      <InvoiceFormInner id={id} />
    </RequirePermission>
  );
}

export function InvoiceFormPage(): ReactNode {
  return <InvoiceFormRoute />;
}
